import { URLS } from '@/data';
import { SelectionRef } from '@/feature/bet-share-sbk/utils/resolve-selections';
import { useUserStore } from '@/hooks/use-user-store';
import { logger } from '@/utils/logging';

const LOG_TAG = '[LinkShare]';

const SHORT_ENTRY_LINK = `${URLS.APP_LINKS_URL}/e/`;
const DEEP_LINK = 'betrpickem://entry/';
const DEEP_LINK_PATH = 'entry/';

type SocialLinkBase = {
    user_id: string;
    user_email: string;
    meta?: {
        title?: string;
        desc?: string;
    };
};

export type FantasySocialLink = SocialLinkBase & { type: 'fantasy'; payload: { entryId: string } };
// app-link-service's wire values; it rejects anything else, so 'sbk' stays even though it means Sweeps here
export type BetSocialLinkType = 'sbk' | 'predictions';
// entryId doubles as the link service's dedupe key; without it every share collapses to one short link
export type BetSocialLink = SocialLinkBase & {
    type: BetSocialLinkType;
    payload: { entryId: string; selections: SelectionRef[] };
};
export type SocialLink = FantasySocialLink | BetSocialLink;

export const isBetSocialLink = (socialLink?: SocialLink): socialLink is BetSocialLink =>
    socialLink?.type === 'sbk' || socialLink?.type === 'predictions';

export type CreateLinkResponse = {
    id: string;
    shortUrl: string;
};

export const isSocialShareLink = (url: string | null) => {
    if (url?.startsWith(SHORT_ENTRY_LINK)) {
        return true;
    } else if (url?.startsWith(DEEP_LINK)) {
        return true;
    } else if (url?.startsWith(DEEP_LINK_PATH)) {
        return true;
    }
    return false;
};

/**
 * Extracts the shortID from the given URL.
 * The URLs can be deeplink (betrpickem://...) or universal links (l.betr.app/...) and the short id will be the last on the path
 */
export const extractShortIdFromURL = (url: string | null): string | undefined => {
    if (url?.startsWith(DEEP_LINK) || url?.startsWith('entry/')) {
        const fullId = url.split('/').pop();
        return fullId;
    } else if (url?.startsWith(SHORT_ENTRY_LINK)) {
        const shortId = new URL(url).pathname.split('/').pop();
        return shortId;
    }
    return undefined;
};

export const importSocialLink = async (shortId: string): Promise<SocialLink | undefined> => {
    const response = await fetch(`${URLS.APP_LINKS_URL}/api/link/${shortId}`, {
        headers: { Authorization: useUserStore.getState().session?.access_token ?? '' },
    });
    const entryData = await response.json();
    if (!response.ok) {
        logger.warn(LOG_TAG, 'Can not fetch shortlink data.', shortId);
        throw Error('Can not fetch shortLink data!');
    }
    return entryData;
};

export const createSocialLink = async (link: SocialLink): Promise<CreateLinkResponse> => {
    return await (
        await fetch(`${URLS.APP_LINKS_URL}/api/link`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: useUserStore.getState().session?.access_token ?? '',
            },
            body: JSON.stringify(link),
        })
    ).json();
};

export const uploadImageMetaToLink = async (linkShortId: string, fileUri: string) => {
    try {
        const formData = new FormData();
        formData.append('file', {
            name: `${linkShortId}.jpg`,
            uri: fileUri,
            type: 'image/jpg',
        });
        const response = await fetch(`${URLS.APP_LINKS_URL}/api/link/upload?refId=${linkShortId}`, {
            method: 'POST',
            headers: {
                Authorization: useUserStore.getState().session?.access_token ?? '',
            },
            // using 'as any' since react-native fetch / form data types are not working for this as of 0.79
            // but the functionality & upload works as expected
            body: formData as any,
        });
        const data = await response.json();
        return data?.url;
    } catch (e) {
        logger.warn('Upload Image', e);
    }
};

export type ShareReferrer = { linkId?: string; entryId?: string };

export const markLinkConversion = ({
    referrer,
    amount,
    entryId,
}: {
    referrer: ShareReferrer;
    amount: number;
    entryId: string;
}) => {
    const { profile, session } = useUserStore.getState();
    const body = {
        entry_id: referrer.entryId,
        user_id: profile.sub,
        user_email: profile.email,
        link_id: referrer.linkId,
        conversion_entry_id: entryId,
        wager: amount,
    };
    fetch(`${URLS.APP_LINKS_URL}/api/link/conversion`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: session?.access_token ?? '',
        },
        body: JSON.stringify(body),
    }).catch(e => logger.warn(LOG_TAG, 'Link conversion failed', e));
};
